import React, { useMemo, useRef, useCallback, useEffect } from 'react';
import GanttHeader from './components/GanttHeader';
import TimelineHeader from './components/TimelineHeader';
import SwimlaneGroup from './components/SwimlaneGroup';
import TaskRow from './components/TaskRow';
import GanttZoomHUD from './components/GanttZoomHUD';
import DependencyLines from './components/DependencyLines';
import FokusOverlay from '../shared/FokusOverlay';
import { calculateDateRange, getTaskDates, groupTasksByColorBy } from './utils';
import { sortTasks } from '../components/SortDropdown';
import { useCampaignProgressStats } from '../shared/CampaignHeader/hooks/useCampaignProgressStats';
import { 
  useGanttZoom, useGanttPan, useGanttPresets, useGanttWheel, 
  useGanttEvents, useGanttResize, useGanttSelection, useGanttClipboard,
  useGanttFiltering, useGanttMultiDrag, useGanttToday, useGanttCursor,
} from './hooks';
import './styles/index.css';

/**
 * GanttView Component
 * 
 * Timeline-based view for tasks showing dependencies and scheduling.
 */
const GanttView = ({
  tasks = [],
  statuses = [],
  allTags = [],
  customFields = [],
  // Campaign header props
  campaignId = '',
  campaignName = '',
  campaignIcon = null,
  campaignProgress = 0,
  breadcrumbPath = [],
  onSelectSprint,
  onSprintIconChange,
  onRenameSprint,
  // Task actions
  onTaskClick,
  onTaskDateChange,
  onDueDateChange,
  onRenameTask,
  onDeleteTasks,
  onPasteTasks,
  // Add task/decision handlers
  onAddTask,
  onAddDecision,
  onAddWait,
  // Decision answer/options handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Swimlane settings (same as Kanban - 'none' by default)
  swimlaneSetting = 'none',
  // Sort settings (same as Kanban - applies within swimlanes)
  sortSetting = 'manual',
  // Nested sprints for actionable filter
  nestedSprints = [],
  localSprint = null,
  allSprints = [],
  // Actionable only filter
  showActionableOnly = false,
  // Filter settings
  filters = {},
  // Selection for Fokus mode (controlled from parent)
  selectedTaskIds: externalSelectedIds,
  onSelectionChange,
  // Fokus mode state
  isFokusMode = false,
  // Archive settings
  showArchived = false,
}) => {
  // Ref for the scrollable chart area
  const chartRef = useRef(null);

  // ===== Filtering =====
  const { filteredTasks, lockStates } = useGanttFiltering({
    tasks, nestedSprints, statuses, filters, showArchived, showActionableOnly, customFields,
    localSprint, allSprints,
  });

  // ===== Zoom & Pan Hooks =====
  const { zoomScale, setZoomScale, zoomLevel, isAnimating, smoothZoom } = useGanttZoom(chartRef);
  const { isPanning, hasPannedRef, handleMouseDown } = useGanttPan(chartRef);
  const { viewPreset, extraRange, handlePresetChange } = useGanttPresets(chartRef, tasks, setZoomScale, zoomScale);

  // Calculate the date range to display (always based on ALL tasks + today + extraRange)
  // IMPORTANT: We pass null for zoomLevel to use fixed padding - this prevents the 
  // timeline boundaries from shifting during zoom (which causes the "jolt" effect)
  const dateRange = useMemo(() => {
    return calculateDateRange(tasks, null, extraRange);
  }, [tasks, extraRange]);

  // Window event handling (toolbar buttons, presets)
  useGanttEvents(chartRef, dateRange, handlePresetChange, smoothZoom);
  
  // Wheel event handling
  useGanttWheel(chartRef, smoothZoom);
  
  // Resize handling - maintain position when container resizes (not zoom changes)
  useGanttResize(chartRef, dateRange);
  
  // ===== Selection & Clipboard =====
  const { 
    selectedTaskIds: internalSelectedIds, 
    selectionBox, 
    clearSelection,
    selectTask,
    toggleTaskSelection,
    handleSelectionMouseDown 
  } = useGanttSelection(chartRef, filteredTasks, dateRange, onDeleteTasks);

  // Use external selection if provided, otherwise use internal
  const selectedTaskIds = externalSelectedIds || internalSelectedIds;

  // Setter for selection (works with both internal and external)
  const setSelectedTaskIds = useCallback((newSelection) => {
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  }, [onSelectionChange]);

  // Sync internal selection changes to parent
  useEffect(() => {
    if (onSelectionChange && internalSelectedIds) {
      onSelectionChange(internalSelectedIds);
    }
  }, [internalSelectedIds, onSelectionChange]);

  // Scroll to task helper (for paste navigation)
  const scrollToTask = useCallback((taskId) => {
    const rowElement = document.querySelector(`.gantt-row[data-gantt-task-id="${taskId}"]`);
    const barElement = document.querySelector(`.gantt-bar[data-task-id="${taskId}"]`);
    
    if (rowElement) {
      // Scroll row vertically into center
      rowElement.scrollIntoView({ behavior: 'instant', block: 'center' });
      
      // Also scroll bar horizontally into center if it exists
      if (barElement) {
        barElement.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'center' });
      }
      
      // Flash highlight the row and bar immediately after instant scroll
      rowElement.classList.add('gantt-row--highlighted');
      if (barElement) {
        barElement.classList.add('gantt-bar--highlighted');
      }
      setTimeout(() => {
        rowElement.classList.remove('gantt-row--highlighted');
        if (barElement) {
          barElement.classList.remove('gantt-bar--highlighted');
        }
      }, 1000);
    }
  }, []);

  // Clipboard handling (Ctrl+C, Ctrl+V, Ctrl+D)
  useGanttClipboard({
    tasks: filteredTasks,
    selectedTaskIds,
    setSelectedTaskIds,
    nestedSprints,
    allSprints,
    allTags,
    customFields,
    onPasteTasks,
    scrollToTask,
  });

  const {
    completedCount,
    totalCount,
    remainingMinutes,
    remainingMinMinutes,
    missingEstCount,
    remainingBreakdown,
  } = useCampaignProgressStats(tasks, statuses, nestedSprints, allSprints);

  // ===== Today Marker & Cursor =====
  const { todayPosition, scrollToToday } = useGanttToday(dateRange, chartRef);
  const { cursorPosition, handleTimelineClick } = useGanttCursor(chartRef, hasPannedRef, clearSelection);

  // ===== Date Change & Multi-Drag =====
  const handleDateChange = useCallback((taskId, newStart, newEnd, isCommit) => {
    if (onTaskDateChange) {
      onTaskDateChange(taskId, newStart, newEnd, isCommit);
    }
  }, [onTaskDateChange]);

  const { multiDragDelta, isDraggingMultiple, multiDragType, handleMultiDrag } = useGanttMultiDrag({
    selectedTaskIds, filteredTasks, onTaskDateChange,
  });

  // Prevent context menu on the gantt chart
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
  }, []);

  // ===== Swimlanes & Sorting =====
  // Group tasks into swimlanes based on swimlaneSetting
  // Returns null when swimlaneSetting is 'none' (flat list)
  const swimlaneGroups = useMemo(() => {
    return groupTasksByColorBy(filteredTasks, swimlaneSetting, statuses, allTags, sortSetting, customFields);
  }, [filteredTasks, swimlaneSetting, statuses, allTags, sortSetting, customFields]);

  // For flat list mode, apply sorting to all tasks
  const sortedFlatTasks = useMemo(() => {
    if (swimlaneGroups) return null;
    return sortTasks(filteredTasks, sortSetting, customFields);
  }, [filteredTasks, sortSetting, swimlaneGroups, customFields]);

  // Build class name with fokus mode
  const viewClassName = `gantt-view${isFokusMode ? ' gantt-view--fokus-mode' : ''}`;

  // ===== Empty State =====
  if (tasks.length === 0 || !dateRange) {
    return (
      <div className={viewClassName}>
        <GanttHeader
          campaignName={campaignName}
          campaignIcon={campaignIcon}
          campaignProgress={campaignProgress}
          breadcrumbPath={breadcrumbPath}
          completedCount={completedCount}
          totalCount={totalCount}
          remainingMinutes={remainingMinutes}
          remainingMinMinutes={remainingMinMinutes}
          missingEstCount={missingEstCount}
          remainingBreakdown={remainingBreakdown}
          onSelectSprint={onSelectSprint}
          onSprintIconChange={onSprintIconChange}
          onRenameSprint={onRenameSprint}
        />
        <div className="gantt-view__empty">
          <h3>No tasks to display</h3>
          <p>Add tasks with dates to see them on the timeline</p>
        </div>
      </div>
    );
  }

  const { startDate, endDate, totalDays } = dateRange;

  // Calculate timeline min-width based on continuous zoom scale
  const timelineMinWidth = Math.max(800, totalDays * 40 * zoomScale);

  return (
    <div className={viewClassName}>
      {/* FOKUS mode overlay */}
      {isFokusMode && <FokusOverlay skipEnterAnimation />}
      
      {/* Campaign header */}
      <GanttHeader
        campaignName={campaignName}
        campaignIcon={campaignIcon}
        campaignProgress={campaignProgress}
        breadcrumbPath={breadcrumbPath}
        completedCount={completedCount}
        totalCount={totalCount}
        remainingMinutes={remainingMinutes}
        remainingMinMinutes={remainingMinMinutes}
        missingEstCount={missingEstCount}
        remainingBreakdown={remainingBreakdown}
        onSelectSprint={onSelectSprint}
        onSprintIconChange={onSprintIconChange}
        onRenameSprint={onRenameSprint}
      />

      {/* Zoom HUD */}
      <GanttZoomHUD />

      {/* Gantt chart area */}
      <div 
        className={`gantt-view__chart ${isPanning ? 'gantt-view__chart--panning' : ''} ${isAnimating ? 'gantt-view__chart--animating' : ''}`}
        ref={chartRef} 
        data-total-days={totalDays}
        onClick={handleTimelineClick}
        onMouseDown={(e) => {
          handleMouseDown(e);
          handleSelectionMouseDown(e);
        }}
        onContextMenu={handleContextMenu}
      >
        {/* Timeline header */}
        <TimelineHeader
          startDate={startDate}
          endDate={endDate}
          totalDays={totalDays}
          zoomLevel={zoomLevel}
          todayPosition={todayPosition}
          cursorPosition={cursorPosition}
          timelineMinWidth={timelineMinWidth}
        />

        {/* Task list */}
        <div className="gantt-view__task-list">
          {swimlaneGroups ? (
            swimlaneGroups.map(group => (
              <SwimlaneGroup
                key={group.key}
                groupKey={group.key}
                label={group.label}
                color={group.color}
                tasks={group.tasks}
                lockStates={lockStates}
                viewStartDate={startDate}
                totalDays={totalDays}
                statuses={statuses}
                allTags={allTags}
                colorBy={swimlaneSetting}
                todayPosition={todayPosition}
                cursorPosition={cursorPosition}
                onDateChange={handleDateChange}
                onDueDateChange={onDueDateChange}
                timelineMinWidth={timelineMinWidth}
                selectedTaskIds={selectedTaskIds}
                onTaskSelect={selectTask}
                onTaskToggleSelect={toggleTaskSelection}
                onMultiDrag={handleMultiDrag}
                multiDragDelta={multiDragDelta}
                isDraggingMultiple={isDraggingMultiple}
                multiDragType={multiDragType}
                zoomLevel={zoomLevel}
                onAddTask={onAddTask}
                onAddDecision={onAddDecision}
                onAddWait={onAddWait}
                onDecisionAnswerChange={onDecisionAnswerChange}
                onDecisionOptionsChange={onDecisionOptionsChange}
                onRenameTask={onRenameTask}
                nestedSprints={nestedSprints}
                allSprints={allSprints}
              />
            ))
          ) : (
            (sortedFlatTasks || filteredTasks).filter(task => getTaskDates(task)).map(task => {
              const taskDates = getTaskDates(task);
              const lockState = lockStates[task.id];
              const isLocked = lockState?.isLocked && task.status !== 'completed';
              return (
                <TaskRow
                  key={task.id}
                  task={task}
                  taskDates={taskDates}
                  isLocked={isLocked}
                  lockReason={lockState?.lockReason}
                  viewStartDate={startDate}
                  totalDays={totalDays}
                  statuses={statuses}
                  allTags={allTags}
                  colorBy={swimlaneSetting}
                  todayPosition={todayPosition}
                  cursorPosition={cursorPosition}
                  onDateChange={handleDateChange}
                  onDueDateChange={onDueDateChange}
                  timelineMinWidth={timelineMinWidth}
                  isSelected={selectedTaskIds.has(task.id)}
                  onSelect={selectTask}
                  onToggleSelect={toggleTaskSelection}
                  selectedTaskIds={selectedTaskIds}
                  onMultiDrag={handleMultiDrag}
                  multiDragDelta={multiDragDelta}
                  isDraggingMultiple={isDraggingMultiple}
                  multiDragType={multiDragType}
                  zoomLevel={zoomLevel}
                  onDecisionAnswerChange={onDecisionAnswerChange}
                  onDecisionOptionsChange={onDecisionOptionsChange}
                  onRenameTask={onRenameTask}
                  nestedSprints={nestedSprints}
                  allSprints={allSprints}
                />
              );
            })
          )}
        </div>

        {/* Dependency lines connecting tasks - rendered above task list */}
        <DependencyLines
          tasks={filteredTasks}
          chartRef={chartRef}
        />

        {/* Selection box overlay */}
        {selectionBox && (
          <div 
            className="gantt-selection-box"
            style={{
              left: selectionBox.left,
              top: selectionBox.top,
              width: selectionBox.width,
              height: selectionBox.height,
            }}
          />
        )}
      </div>
    </div>
  );
};

export default GanttView;
